import { z } from 'zod';

import { CounterpartySnapshotSchema } from '../counterparties/counterparties.schema.js';
import {
  ShipmentReportDailyRowSchema,
  ShipmentReportTotalRowSchema,
} from './shipments-shared.schema.js';
import {
  IsoDateSchema,
  IsoDateTimeSchema,
} from './shipments-types.js';
import type {
  ShipmentFormalReportAttachmentRegisterRow,
  ShipmentFormalReportAttachmentRegisterSection,
  ShipmentFormalReportDailySummarySection,
  ShipmentFormalReportDocument,
  ShipmentFormalReportDoriisGuidance,
  ShipmentFormalReportEvidenceSummary,
  ShipmentFormalReportMaterial,
  ShipmentFormalReportPeriod,
  ShipmentFormalReportProvenance,
  ShipmentFormalReportShippingValuesSection,
  ShipmentFormalReportSignatureBlock,
} from './formal-shipping-report-document.dto.js';
import {
  shipmentFormalReportDailySummarySourceValues,
  shipmentFormalReportDocumentTypeValues,
  shipmentFormalReportDoriisEvidenceStatusValues,
  shipmentFormalReportStatusValues,
} from './formal-shipping-report-document.dto.js';
import { ReportableWeightLbSchema } from '../reportable-weight.schema.js';

const nonNegativeNumberSchema = z.number().min(0);
const nonNegativeIntSchema = z.number().int().min(0);
const trimmedStringSchema = z.string().trim().min(1);

export const ShipmentFormalReportMaterialSchema: z.ZodType<ShipmentFormalReportMaterial> = z
  .object({
    materialId: z.uuid(),
    displayName: trimmedStringSchema.nullable(),
  })
  .strict();

export const ShipmentFormalReportPeriodSchema: z.ZodType<ShipmentFormalReportPeriod> = z
  .object({
    rangeStartAt: IsoDateTimeSchema,
    rangeEndAt: IsoDateTimeSchema,
  })
  .strict();

export const ShipmentFormalReportProvenanceSchema: z.ZodType<ShipmentFormalReportProvenance> = z
  .object({
    sourceLabel: trimmedStringSchema,
    packetLabel: trimmedStringSchema,
    notDoriisUploadFile: z.literal(true),
    notDoriisSubmissionProofUnlessLinkedEvidence: z.literal(true),
    notes: z.array(trimmedStringSchema),
  })
  .strict();

export const ShipmentFormalReportDailySummarySectionSchema: z.ZodType<ShipmentFormalReportDailySummarySection> = z
  .object({
    source: z.enum(shipmentFormalReportDailySummarySourceValues),
    rows: z.array(ShipmentReportDailyRowSchema),
    totalRow: ShipmentReportTotalRowSchema,
  })
  .strict();

export const ShipmentFormalReportShippingValuesSectionSchema: z.ZodType<ShipmentFormalReportShippingValuesSection> = z
  .object({
    totalRow: ShipmentReportTotalRowSchema,
    attachedReportCount: nonNegativeIntSchema,
    totalDocumentWeightLb: ReportableWeightLbSchema,
    totalRefundValueCents: nonNegativeIntSchema,
  })
  .strict();

export const ShipmentFormalReportAttachmentRegisterRowSchema: z.ZodType<ShipmentFormalReportAttachmentRegisterRow> = z
  .object({
    sourceShippingReportNumber: trimmedStringSchema.nullable(),
    sourceShipper: CounterpartySnapshotSchema,
    receivedAt: IsoDateTimeSchema,
    officialContributionWeightLb: ReportableWeightLbSchema,
    officialContributionRefundValueCents: nonNegativeIntSchema,
    physicalReceivedWeightLb: nonNegativeNumberSchema,
  })
  .strict();

export const ShipmentFormalReportAttachmentRegisterSectionSchema: z.ZodType<ShipmentFormalReportAttachmentRegisterSection> = z
  .object({
    rows: z.array(ShipmentFormalReportAttachmentRegisterRowSchema),
    totalAttachedReportCount: nonNegativeIntSchema,
    totalOfficialContributionWeightLb: ReportableWeightLbSchema,
    totalOfficialContributionRefundValueCents: nonNegativeIntSchema,
  })
  .strict();

export const ShipmentFormalReportEvidenceSummarySchema: z.ZodType<ShipmentFormalReportEvidenceSummary> = z
  .object({
    doriisShippingReportNumber: trimmedStringSchema.nullable(),
    receivedDate: IsoDateSchema.nullable(),
    weightTicketNumber: trimmedStringSchema.nullable(),
    officialReceivedWeightLb: ReportableWeightLbSchema.nullable(),
    processorReceivedWeightLb: nonNegativeNumberSchema.nullable(),
    inboundPhysicalReceivedWeightLb: nonNegativeNumberSchema.nullable(),
    selectedHuPhysicalWeightLb: nonNegativeNumberSchema.nullable(),
    evidenceNotes: z.array(trimmedStringSchema),
  })
  .strict();

export const ShipmentFormalReportDoriisGuidanceSchema: z.ZodType<ShipmentFormalReportDoriisGuidance> = z
  .object({
    evidenceStatus: z.enum(shipmentFormalReportDoriisEvidenceStatusValues),
    message: trimmedStringSchema,
    notDoriisUploadFile: z.literal(true),
  })
  .strict();

export const ShipmentFormalReportSignatureBlockSchema: z.ZodType<ShipmentFormalReportSignatureBlock> = z
  .object({
    preparerName: trimmedStringSchema.nullable(),
    preparerTitle: trimmedStringSchema.nullable(),
    signatureDate: IsoDateSchema.nullable(),
  })
  .strict();

export const ShipmentFormalReportDocumentSchema: z.ZodType<ShipmentFormalReportDocument> = z
  .object({
    documentType: z.enum(shipmentFormalReportDocumentTypeValues),
    documentTitle: trimmedStringSchema,
    generatedAt: IsoDateTimeSchema,
    generatedBy: trimmedStringSchema.nullable(),
    status: z.enum(shipmentFormalReportStatusValues),
    completenessWarnings: z.array(trimmedStringSchema),
    provenance: ShipmentFormalReportProvenanceSchema,
    shipper: CounterpartySnapshotSchema,
    receiver: CounterpartySnapshotSchema,
    reportPeriod: ShipmentFormalReportPeriodSchema,
    material: ShipmentFormalReportMaterialSchema,
    dailySummary: ShipmentFormalReportDailySummarySectionSchema,
    shippingReportValues: ShipmentFormalReportShippingValuesSectionSchema,
    coverAttachmentRegister: ShipmentFormalReportAttachmentRegisterSectionSchema.nullable(),
    evidenceSummary: ShipmentFormalReportEvidenceSummarySchema,
    doriisGuidance: ShipmentFormalReportDoriisGuidanceSchema,
    signatureBlock: ShipmentFormalReportSignatureBlockSchema,
  })
  .strict();
